/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from "react";
import Select from "react-select";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Dropdown from "react-bootstrap/Dropdown";
import Collapse from "react-bootstrap/Collapse";
import ProductOrderDetail from "pages/pos-menus/pos/ProductOrderDetail";
import RealtimeDateTime from "../../../components/fragments/RealtimeDateTime";
import MemberSelectedCard from "../../../components/modals/MemberSelectedCard";
import { memberHandler } from "../../../redux/handlers";
import { useDispatch } from "react-redux";
import { updateCurrentOrder } from "../../../redux/slices/pos.slice";
import { useOrderSocket } from "../../../hooks/useOrderSocket";

if (!window.hasOwnProperty("isChangingDeliveryType")) {
  window.isChangingDeliveryType = false;
}

const updateProductPricesBasedOnDelivery = (
  products,
  deliveryType,
  availableProducts
) => {
  if (!products || !Array.isArray(products) || !availableProducts)
    return products;

  return products.map((product) => {
    const productInfo = availableProducts.find(
      (p) => p.productID === product.productID
    );

    if (!productInfo || !productInfo.product) return product;

    const useDeliveryPrice = deliveryType === 3;
    const basePrice = useDeliveryPrice
      ? parseFloat(
          productInfo.data?.price_delivery ||
            productInfo.product?.price_delivery ||
            productInfo.product.price
        )
      : parseFloat(productInfo.data?.price || productInfo.product?.price);

    let optionsPriceTotal = 0;
    if (product.standardOptions) {
      optionsPriceTotal += product.standardOptions.reduce((sum, option) => {
        return (
          sum +
          parseFloat(option.itemPrice || 0) * parseInt(option.optionQty || 1)
        );
      }, 0);
    }
    if (product.specificOptions) {
      optionsPriceTotal += product.specificOptions.reduce((sum, option) => {
        return (
          sum +
          parseFloat(option.itemPrice || 0) * parseInt(option.optionQty || 1)
        );
      }, 0);
    }

    const unitPrice = basePrice + optionsPriceTotal;
    const totalPrice = unitPrice * (parseInt(product.productQty) || 1);

    return {
      ...product,
      productPrice: basePrice,
      productSumPrice: totalPrice,
      displayPrice: totalPrice.toFixed(2),
    };
  });
};

const Order = ({
  orderData,
  orderId,
  setOrderData,
  billData,
  handleEditProduct,
  handleHoldBill,
  handlePayment,
  updateModalState,
  submitOrders,
  resetOrder,
  deliveryOptions,
  setmodalSearchMemberOpen,
  businessSlug,
  availableProducts,
  onOpenOrderRemark,
}) => {
  const dispatch = useDispatch();
  const isUpdatingState = useRef(false);

  const [isTotalExpanded, setIsTotalExpanded] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [dynamicTableOptions, setDynamicTableOptions] = useState([]);
  const { connect, disconnect } = useOrderSocket();

  const hasUnsentOrders = orderData.product?.some(
    (product) => product.productStatus === 0
  );

  const disablePayment =
    (orderData.orderPrice && orderData.orderPrice.sumprice === 0) ||
    orderData.orderNo === "" ||
    orderData.billStatus === "COMPLETED" ||
    hasUnsentOrders;

  const handleDeliveryChange = useCallback(
    (selectedOption) => {
      const newDeliveryType = selectedOption.value;

      // ป้องกันการเปลี่ยน order_type ซ้ำ
      if (newDeliveryType === orderData.delivery) {
        return;
      }

      window.isChangingDeliveryType = true;
      isUpdatingState.current = true;

      if (orderData.orderNo) {
        resetOrder();

        setTimeout(() => {
          dispatch(
            updateCurrentOrder({
              delivery: newDeliveryType,
            })
          );

          window.isChangingDeliveryType = false;
          isUpdatingState.current = false;
        }, 100);
      } else {
        const updatedProducts = updateProductPricesBasedOnDelivery(
          orderData.product,
          newDeliveryType,
          availableProducts
        );

        const totalOrderAmount = calculateOrderTotal(updatedProducts);

        dispatch(
          updateCurrentOrder({
            delivery: newDeliveryType,
            product: updatedProducts,
            orderPrice: {
              ...orderData.orderPrice,
              sumprice: totalOrderAmount,
              net: totalOrderAmount,
            },
          })
        );

        setTimeout(() => {
          window.isChangingDeliveryType = false;
          isUpdatingState.current = false;
        }, 100);
      }
    },
    [orderData, resetOrder, availableProducts, dispatch]
  );

  const handleTableSelect = useCallback(
    (e) => {
      isUpdatingState.current = true;

      dispatch(
        updateCurrentOrder({
          tableID: e.value,
        })
      );

      setTimeout(() => {
        isUpdatingState.current = false;
      }, 100);
    },
    [dispatch]
  );

  useEffect(() => {
    if (orderData.tableDetails && orderData.tableID) {
      const tableOption = {
        value: orderData.tableID,
        label: orderData.orderNo
          ? `VIP - ${orderData.tableDetails.tableName}`
          : `VIP ${orderData.tableDetails.tableNo}`,
      };
      setDynamicTableOptions([tableOption]);
    }
  }, [orderData, orderData.tableDetails, orderData.tableID]);

  useEffect(() => {
    if (orderData.memberID) {
      dispatch(memberHandler.getMemberById(businessSlug, orderData.memberID))
        .then((result) => {
          if (result) {
            setMemberData(result);
          }
        })
        .catch((error) => {
          console.error("Error fetching member details:", error);
        });
    } else {
      setMemberData(null);
    }
  }, [orderData.memberID, dispatch, businessSlug]);

  const handleClearMember = useCallback(() => {
    isUpdatingState.current = true;

    if (orderData.memberID && orderId) {
      const requestData = {
        businessSlug: businessSlug,
        id: orderData.memberID,
      };

      dispatch(
        memberHandler.removeOrderMember(businessSlug, orderId, requestData)
      )
        .then(() => {
          dispatch(
            updateCurrentOrder({
              memberID: null,
              memberName: "",
            })
          );

          setMemberData(null);
          isUpdatingState.current = false;
        })
        .catch((error) => {
          console.error("Error removing member from order:", error);

          dispatch(
            updateCurrentOrder({
              memberID: null,
              memberName: "",
            })
          );

          setMemberData(null);
          isUpdatingState.current = false;
        });
    } else {
      dispatch(
        updateCurrentOrder({
          memberID: null,
          memberName: "",
        })
      );

      setMemberData(null);

      setTimeout(() => {
        isUpdatingState.current = false;
      }, 100);
    }
  }, [orderData.memberID, orderId, businessSlug, dispatch]);

  const calculateOrderTotal = useCallback((products) => {
    if (!products || !Array.isArray(products)) return 0;

    return products
      .filter((item) => item.productStatus !== 4)
      .reduce((sum, item) => sum + parseFloat(item.productSumPrice || 0), 0);
  }, []);

  const memberInfo = memberData
    ? {
        avatar: "/img/icons/icon-user-green.svg",
        name: `${memberData.first_name_th || ""} ${
          memberData.last_name_th || ""
        }`.trim(),
        phoneNumber: memberData.phone_number || "-",
        position: "MEMBER",
        point: memberData.total_point || 0,
      }
    : null;

  useEffect(() => {
    if (orderData?.orderID?.current != null) {
      connect(orderData?.orderID.current);
    } else {
      disconnect();
    }
  }, [connect, disconnect, orderData]);

  useEffect(() => {
    if (window) {
      window.isOrderComponentUpdating = isUpdatingState.current;
    }
  }, [isUpdatingState.current]);

  const formatAmount = (num) => {
    const numStr = num.toString();
    const [intPart, decimalPart = ""] = numStr.split(".");
    let formattedDecimal;
    if (decimalPart.length === 0) {
      formattedDecimal = "00";
    } else if (decimalPart.length === 1) {
      formattedDecimal = decimalPart + "0";
    } else {
      formattedDecimal = decimalPart.substring(0, 2);
    }
    return `${intPart}.${formattedDecimal}`;
  };

  const onCallBill = useCallback(() => {
    if (window.isProcessingBillRecall) {
      return;
    }
    window.isProcessingBillRecall = true;
    updateModalState("isCallBillModalOpen", true);

    setTimeout(() => {
      window.isProcessingBillRecall = false;
    }, 100);
  }, [updateModalState]);

  return (
    <div className="customShow">
      <div className="ordertop py-2 px-2">
        <div className="fw-5 tc-8">
          ORDER NO. <span className="tc-6 ms-3">{orderData.orderNo}</span>
        </div>
        <RealtimeDateTime />
        {memberData ? (
          <div className="mt-2">
            <MemberSelectedCard
              info={memberInfo}
              handleClear={handleClearMember}
            />
          </div>
        ) : (
          <button
            className="btn btn-add-member"
            onClick={() => setmodalSearchMemberOpen(true)}
          >
            เพิ่มสมาชิก
          </button>
        )}
      </div>

      <div className="d-flex align-items-center px-2 ">
        <div className="form-select-image">
          <Select
            className={`w-100 selectedimg-${orderData.delivery}`}
            options={deliveryOptions}
            onChange={handleDeliveryChange}
            value={deliveryOptions?.filter(
              (item) => item.value === orderData.delivery
            )}
            placeholder=""
          />
        </div>

        {orderData.delivery === 1 ? (
          <>
            <button
              className="btn btn-desk mx-1"
              onClick={() => updateModalState("isTableSelectModalOpen", true)}
            >
              โต๊ะ
            </button>
            <div className="w-100 form-select-st-2">
              <Select
                className="w-100"
                options={dynamicTableOptions}
                placeholder="ยังไม่เลือกโต๊ะ"
                onChange={handleTableSelect}
                value={dynamicTableOptions?.filter(
                  (item) => item.value === orderData.tableID
                )}
              />
            </div>
          </>
        ) : (
          ""
        )}

        {(orderData.delivery === 2 || orderData.delivery === 3) && (
          <>
            <button
              className="btn btn-pencil mx-2"
              onClick={onOpenOrderRemark}
            ></button>
            <div className="pl-2 text-md">{orderData.remark}</div>
          </>
        )}
      </div>

      <div>
        <div className="py-2">
          <div className="px-2">
            <div className="row gx-2 tc-17 fs-14">
              <div className="col-7">รายการ</div>
              <div className="col-2 text-center">จำนวน</div>
              <div className="col-3 text-end">ราคา</div>
            </div>
          </div>
          <hr className="mt-1 mb-0" />

          <div className="bx-main-order-list ">
            <ul className="p-0 m-0 w-100 ">
              <ProductOrderDetail
                products={orderData.product}
                handleClick={handleEditProduct}
              />
            </ul>
          </div>

          <div className="text-center">
            {orderData.product?.filter((i) => i.productStatus === 0)?.length >
              0 && (
              <button
                className={`btn btn-st-10 btn-send-order mt-2 ${
                  orderData.delivery === 1 && !orderData.tableID
                    ? "disabled opacity-50"
                    : ""
                }`}
                onClick={submitOrders}
                disabled={orderData.delivery === 1 && !orderData.tableID}
                title={
                  orderData.delivery === 1 && !orderData.tableID
                    ? "กรุณาเลือกโต๊ะก่อนส่งออเดอร์"
                    : ""
                }
              >
                ส่งออเดอร์
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="orderbottom pb-2 px-0">
        <Collapse in={isTotalExpanded}>
          <div id="collapse1">
            <div className="card card-body border-0 bg-c1 px-3 py-2">
              <div className="d-flex fs-14 tc-10 px-1">
                <div className="me-auto">ยอดรวม</div>
                <div className="" id="txsum">
                  {Number(orderData.orderPrice?.sumprice).toFixed(2)}฿
                </div>
              </div>
              <div className="d-flex fs-12 tc-10 px-1">
                <div className="me-auto">มูลค่าสินค้าไม่รวมภาษี</div>
                <div id="txdiscount">
                  {formatAmount(billData.taxable_amount)}฿
                </div>
              </div>
              <div className="d-flex fs-12 tc-10 px-1">
                <div className="me-auto">ภาษี</div>
                <div id="txtax">{formatAmount(billData.vat)}฿</div>
              </div>
            </div>
          </div>
        </Collapse>

        <button
          className="btn collapse-total collapsed mb-3"
          type="button"
          onClick={() => setIsTotalExpanded(!isTotalExpanded)}
          aria-controls="collapse1"
          aria-expanded={isTotalExpanded}
        >
          <div className="d-flex align-items-center">
            <div className="me-auto">ยอดรวมทั้งหมด </div>
            <div id="txnet">
              {Number(orderData.orderPrice?.net).toFixed(2)}฿
            </div>
          </div>
        </button>

        <div className="d-flex align-items-center justify-content-between px-2 mb-1">
          <Dropdown as={ButtonGroup}>
            <button className="btn-st-6 btn" onClick={onCallBill}>
              เรียกบิล
            </button>
            <Dropdown.Toggle split className="i-ar" id="dropdown-split-basic" />
            <Dropdown.Menu>
              <div
                className="d-flex align-items-center"
                style={{ marginLeft: "50px" }}
              >
                <button
                  className={`  ${
                    orderData.orderNo !== ""
                      ? "bg-[#D3D3D3] rounded-[8px] font-bold p-[10px] text-nowrap flex-none w-[104.88px] "
                      : " btn-pause-bill btn text-white"
                  } `}
                  onClick={handleHoldBill}
                  disabled={orderData.orderNo !== ""}
                >
                  พักบิล
                </button>
                <button className="btn btn-st-7 ms-1" onClick={resetOrder}>
                  ล้างรายการ
                </button>
              </div>
            </Dropdown.Menu>
          </Dropdown>

          <div className="ps-2 flex-fill">
            <button
              className={`btn btn-pay btn-payment w-100 ${
                disablePayment ? "disabled" : ""
              }`}
              onClick={handlePayment}
              disabled={disablePayment}
              title={
                hasUnsentOrders
                  ? "กรุณาส่งออเดอร์ก่อนชำระเงิน"
                  : disablePayment
                  ? "บิลนี้ได้ถูกชำระเงินเรียบร้อยแล้ว"
                  : ""
              }
            >
              ชำระเงิน
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Order;
